import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Clock, QrCode, RefreshCw, MapPin, Package,
  UserCheck, UserX, Download, History, Settings,
  AlertCircle, CheckCircle, Truck, Timer
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function FahrerManagement() {
  // State
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingQR, setLoadingQR] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [restaurantLocation, setRestaurantLocation] = useState(null);
  
  // Location Settings
  const [locationForm, setLocationForm] = useState({
    lat: 52.520,
    lng: 13.405,
    address: '',
    radius_meters: 200
  });

  const tenantId = localStorage.getItem('tenant_id') || 'default';

  // ==========================================================================
  // API Functions
  // ==========================================================================

  const loadActiveDrivers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/driver/shift/active-drivers?tenant_id=${tenantId}`);
      const data = await response.json();
      setActiveDrivers(data.active_drivers || []);
    } catch (error) {
      console.error('Error loading active drivers:', error);
    }
  }, [tenantId]);

  const loadShiftHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/driver/shift/history?tenant_id=${tenantId}&days=7`);
      const data = await response.json();
      setShiftHistory(data.shifts || []);
    } catch (error) {
      console.error('Error loading shift history:', error);
    }
  }, [tenantId]);

  const loadQRCode = async () => {
    setLoadingQR(true);
    try {
      const response = await fetch(`${API_BASE}/api/driver/shift/qr-code?tenant_id=${tenantId}`);
      const data = await response.json();
      setQrCodeData(data);
    } catch (error) {
      console.error('Error loading QR code:', error);
      toast.error('Fehler beim Laden des QR-Codes');
    } finally {
      setLoadingQR(false);
    }
  };

  const loadRestaurantLocation = useCallback(async () => {
    try {
      // We need to get location from a public endpoint or settings
      // For now, use defaults
      setRestaurantLocation({
        lat: 52.520,
        lng: 13.405,
        radius_meters: 200,
        address: 'Restaurant Berlin'
      });
    } catch (error) {
      console.error('Error loading restaurant location:', error);
    }
  }, []);

  const saveRestaurantLocation = async () => {
    try {
      const params = new URLSearchParams({
        lat: locationForm.lat,
        lng: locationForm.lng,
        address: locationForm.address,
        radius_meters: locationForm.radius_meters,
        tenant_id: tenantId
      });
      
      const response = await fetch(`${API_BASE}/api/driver/shift/set-restaurant-location?${params}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Restaurant-Standort gespeichert');
        setRestaurantLocation(data.location);
      }
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadActiveDrivers(),
        loadShiftHistory(),
        loadRestaurantLocation()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [loadActiveDrivers, loadShiftHistory, loadRestaurantLocation]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveDrivers();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadActiveDrivers]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="p-6 space-y-6" data-testid="fahrer-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            Fahrer-Management
          </h1>
          <p className="text-gray-400 mt-1">Schichten, QR-Codes und Fahrer-Übersicht</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              loadActiveDrivers();
              loadShiftHistory();
            }}
            className="border-gray-700"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          
          <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={loadQRCode}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="show-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Schicht-QR anzeigen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-purple-500" />
                  Täglicher Schicht-QR-Code
                </DialogTitle>
                <DialogDescription>
                  Fahrer scannen diesen Code um ihre Schicht zu starten.
                  Der Code wechselt automatisch jeden Tag.
                </DialogDescription>
              </DialogHeader>
              
              {loadingQR ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : qrCodeData ? (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-xl flex justify-center">
                    <img 
                      src={qrCodeData.qr_code_image} 
                      alt="Schicht QR Code" 
                      className="w-64 h-64"
                      data-testid="shift-qr-image"
                    />
                  </div>
                  
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-400">
                      Gültig für: <span className="text-white font-medium">{qrCodeData.valid_date}</span>
                    </p>
                    <Badge variant="outline" className="text-green-400 border-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      24 Stunden gültig
                    </Badge>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-yellow-400 text-sm flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Diesen QR-Code im Restaurant aushängen. Fahrer müssen ihn scannen um ihre Schicht zu starten.
                    </p>
                  </div>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      // Download QR code
                      const link = document.createElement('a');
                      link.href = qrCodeData.qr_code_image;
                      link.download = `schicht-qr-${qrCodeData.valid_date}.png`;
                      link.click();
                      toast.success('QR-Code heruntergeladen');
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    QR-Code herunterladen
                  </Button>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">Fehler beim Laden</p>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Fahrer im Dienst</p>
                <p className="text-3xl font-bold text-green-400">{activeDrivers.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Lieferungen heute</p>
                <p className="text-3xl font-bold text-purple-400">
                  {activeDrivers.reduce((sum, d) => sum + (d.deliveries_count || 0), 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">GPS-Radius</p>
                <p className="text-3xl font-bold text-blue-400">{restaurantLocation?.radius_meters || 200}m</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Schichten (7 Tage)</p>
                <p className="text-3xl font-bold text-orange-400">{shiftHistory.length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                <History className="w-6 h-6 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList className="bg-gray-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-purple-600">
            <UserCheck className="w-4 h-4 mr-2" />
            Fahrer im Dienst
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-purple-600">
            <History className="w-4 h-4 mr-2" />
            Schicht-Historie
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-purple-600">
            <Settings className="w-4 h-4 mr-2" />
            Einstellungen
          </TabsTrigger>
        </TabsList>

        {/* Active Drivers Tab */}
        <TabsContent value="active">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Truck className="w-5 h-5 text-green-400" />
                Fahrer aktuell im Dienst
              </CardTitle>
              <CardDescription>Live-Übersicht aller aktiven Fahrer</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                  <p className="text-gray-400 mt-2">Laden...</p>
                </div>
              ) : activeDrivers.length === 0 ? (
                <div className="text-center py-12">
                  <UserX className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400 text-lg">Keine Fahrer im Dienst</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Fahrer müssen den Schicht-QR-Code scannen um anzufangen
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Fahrer</TableHead>
                      <TableHead className="text-gray-400">Schichtbeginn</TableHead>
                      <TableHead className="text-gray-400">Dauer</TableHead>
                      <TableHead className="text-gray-400">Lieferungen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeDrivers.map((driver) => (
                      <TableRow key={driver.shift_id} className="border-gray-800">
                        <TableCell>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                            Im Dienst
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {driver.driver_name}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {driver.started_at_formatted} Uhr
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-purple-400 border-purple-400">
                            <Timer className="w-3 h-3 mr-1" />
                            {driver.duration_formatted}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-blue-400 border-blue-400">
                            <Package className="w-3 h-3 mr-1" />
                            {driver.deliveries_count}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-orange-400" />
                Schicht-Historie (letzte 7 Tage)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shiftHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">Keine Schichten in den letzten 7 Tagen</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Fahrer</TableHead>
                      <TableHead className="text-gray-400">Datum</TableHead>
                      <TableHead className="text-gray-400">Beginn</TableHead>
                      <TableHead className="text-gray-400">Ende</TableHead>
                      <TableHead className="text-gray-400">Dauer</TableHead>
                      <TableHead className="text-gray-400">Lieferungen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shiftHistory.map((shift) => {
                      const startDate = shift.shift_start ? new Date(shift.shift_start) : null;
                      const endDate = shift.shift_end ? new Date(shift.shift_end) : null;
                      const hours = Math.floor((shift.duration_minutes || 0) / 60);
                      const mins = (shift.duration_minutes || 0) % 60;
                      
                      return (
                        <TableRow key={shift.shift_id || shift.id} className="border-gray-800">
                          <TableCell>
                            {shift.status === 'active' ? (
                              <Badge className="bg-green-500/20 text-green-400">
                                Aktiv
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400">
                                Beendet
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-white font-medium">
                            {shift.driver_name}
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {startDate?.toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {startDate?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {endDate?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) || '-'}
                          </TableCell>
                          <TableCell className="text-purple-400">
                            {hours > 0 ? `${hours}h ${mins}min` : `${mins}min`}
                          </TableCell>
                          <TableCell className="text-blue-400">
                            {shift.deliveries_count || 0}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-400" />
                Restaurant-Standort (GPS-Verifizierung)
              </CardTitle>
              <CardDescription>
                Fahrer müssen innerhalb dieses Radius sein um ihre Schicht zu starten/beenden
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Adresse</Label>
                    <Input
                      value={locationForm.address}
                      onChange={(e) => setLocationForm({...locationForm, address: e.target.value})}
                      placeholder="z.B. Hauptstraße 1, 10115 Berlin"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Breitengrad (Lat)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={locationForm.lat}
                        onChange={(e) => setLocationForm({...locationForm, lat: parseFloat(e.target.value)})}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Längengrad (Lng)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={locationForm.lng}
                        onChange={(e) => setLocationForm({...locationForm, lng: parseFloat(e.target.value)})}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Erlaubter Radius (Meter)</Label>
                    <Input
                      type="number"
                      value={locationForm.radius_meters}
                      onChange={(e) => setLocationForm({...locationForm, radius_meters: parseInt(e.target.value)})}
                      className="bg-gray-800 border-gray-700"
                    />
                    <p className="text-xs text-gray-500">Fahrer müssen innerhalb dieses Radius sein</p>
                  </div>
                  
                  <Button
                    onClick={saveRestaurantLocation}
                    className="bg-purple-600 hover:bg-purple-700 w-full"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Standort speichern
                  </Button>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">GPS-Tipp</h4>
                  <ol className="text-gray-400 text-sm space-y-2">
                    <li>1. Öffnen Sie Google Maps auf Ihrem Handy</li>
                    <li>2. Tippen Sie lange auf Ihren Restaurant-Standort</li>
                    <li>3. Kopieren Sie die Koordinaten (z.B. 52.5200, 13.4050)</li>
                    <li>4. Tragen Sie die Werte hier ein</li>
                  </ol>
                  
                  <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 text-sm">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Aktuell: {restaurantLocation?.address || 'Nicht gesetzt'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
